import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_FILE_URL, BASE_URL } from "../../../constant";
import { ToastContainer, toast } from "react-toastify";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { useTranslation } from "react-i18next";
import AppContext from "../../../AppContext";
import { ReactSVG } from "react-svg";

const CompanyForm = () => {
  const currencyData = ["USD", "AED", "INR", "EUR", "GBP", "JPY", "CAD"];
  const defaultCompany = {
    code: "",
    name: "",
    currency: "",
    isSchedulerActive: true,
    isFuelActive: true,
    active: true,
    enabledAssetTypes: [],
  };
  const { t } = useTranslation();
  const { id } = useParams();
  const { darkMode } = useContext(AppContext);
  const navigate = useNavigate();
  const [company, setCompany] = useState(defaultCompany);
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [assetTypes, setAssetTypes] = useState([]);

  const handleChange = (e) => {
    setCompany((pre) => ({ ...pre, [e.target.name]: e.target.value }));
  };

  const onCheckboxChange = (e) => {
    setCompany((pre) => ({ ...pre, [e.target.name]: e.target.checked }));
  };

  const loadData = async () => {
    const res = await axios.get(`${BASE_URL}/api/CompanyMaster/${id}`);
    if (res.status === 200) {
      setCompany({
        ...res.data,
        enabledAssetTypes: res.data.enabledAssetTypes
          ? res.data.enabledAssetTypes
          : [],
      });
    }
  };

  const handleFileChange = async (event) => {
    const config = { headers: { "Content-Type": "multipart/form-data" } };
    const formData = new FormData();
    const selectedFile = event.target.files[0];

    // Check if a file was selected
    if (selectedFile) {
      const image = new Image();
      image.src = URL.createObjectURL(selectedFile);

      image.onload = () => {
        // Aspect ratio is close enough to 16:9
        formData.append("files", selectedFile);
        axios
          .post(`${BASE_URL}/Storage/file/upload`, formData, config)
          .then((res) => {
            setCompany((pre) => ({ ...pre, imageId: res.data[0].id }));
          })
          .catch((error) => {
            // Handle the upload error
            console.error("Error occurred during upload:", error.message);
          });
      };
    } else {
      // No file selected
      toast.error("Please select an image.");
    }
  };

  const onSave = async () => {
    setLoading(true);
    const res = await axios.post(`${BASE_URL}/api/CompanyMaster`, company);
    if (res.status === 200) {
      toast.success("Company Saved Successfully");
      navigate(`/companyForm/${res.data}`);
    } else {
      toast.error("Some Error Occured While Saving");
    }
    setLoading(false);
  };

  const loadAssetTypes = async () => {
    const res = await axios.get(`${BASE_URL}/AssetType/GetAllAssetTypes`);
    if (res.status === 200) {
      setAssetTypes(res.data);
    }
  };

  const onAssetTypeCheckBoxChange = (assetTypeId) => {
    setCompany((pre) => ({
      ...pre,
      enabledAssetTypes: pre.enabledAssetTypes.includes(assetTypeId)
        ? pre.enabledAssetTypes.filter((x) => x !== assetTypeId)
        : [...pre.enabledAssetTypes, assetTypeId],
    }));
  };
  useEffect(() => {
    loadAssetTypes();
    if (id !== "new") loadData();
  }, []);

  return (
    <div className="flex flex-col relative text-white px-14 py-7 dark:bg-darkModeBg bg-lightModeBg h-full">
      <div className="flex text-xs font-medium gap-3 items-center">
        <button type="button" onClick={() => navigate("/companyList")}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke={darkMode ? "white" : "black"}
            className="w-6 h-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
            />
          </svg>
        </button>
        <label className="dark:text-white text-labels">
          {id === "new" ? t("button.addNew") : t("button.update")}
          &nbsp;{t("masters.headerTexts.company")}
        </label>
        <div className="flex h-10 w-10  dark:bg-zinc-750 bg-gray2 justify-center items-center rounded-full">
          <ReactSVG
            src="companyImage.svg"
            className="w-4 flex items-center justify-center"
            beforeInjection={(svg) => {
              svg.setAttribute(
                "style",
                `stroke: ${darkMode ? "white" : "black"}`
              );
            }}
          />
        </div>
      </div>
      <div className="flex">
        <div className="flex flex-col text-xs font-medium py-4 gap-5 w-fit">
          <div className="flex flex-col">
            <label className="dark:text-white text-labels text-sm font-medium py-2">
              {t("masters.fields.code")}
            </label>
            <input
              type="text"
              name="code"
              placeholder="Code"
              onChange={handleChange}
              value={company.code}
              className="block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light rounded-lg dark:bg-zinc-750 bg-gray2 to-zinc-600 text-white px-5 w-full h-9"
            />
          </div>
          <div className="flex flex-col">
            <label className="dark:text-white text-labels text-sm font-medium py-2">
              {t("masters.fields.name")}
            </label>
            <input
              type="text"
              name="name"
              placeholder="Name"
              onChange={handleChange}
              value={company.name}
              className="block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light rounded-lg dark:bg-zinc-750 bg-gray2 to-zinc-600 text-white px-5 w-full h-9"
            />
          </div>
          <div className="flex flex-col">
            <label className="dark:text-white text-labels text-sm font-medium py-2">
              {t("masters.fields.currency")}
            </label>
            <div
              className={`w-40 lg:w-96 h-9 ${
                darkMode ? "darkCombo" : "lightCombo"
              }`}
            >
              <ComboBox
                data={currencyData}
                name="currency"
                suggest={true}
                clearButton={false}
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  paddingLeft: "15px",
                }}
                className=" block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light dark:text-white text-inputFieldsTxts rounded-lg w-full h-full"
                onChange={handleChange}
                value={company.currency}
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 h-full">
            <p className="text-sm font-medium dark:text-white text-labels">
              {t("masters.fields.scheduling")}&nbsp;:
            </p>
            <input
              name="isSchedulerActive"
              type="checkbox"
              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
              id="inlineCheckbox1"
              onChange={onCheckboxChange}
              checked={company.isSchedulerActive}
              value={company.isSchedulerActive}
            />
          </div>
          <div className="flex items-center h-full mt-3 justify-between">
            <p className="text-sm font-medium dark:text-white text-labels">
              {t("masters.fields.fuelManagement")}&nbsp;:
            </p>
            <input
              name="isFuelActive"
              type="checkbox"
              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
              id="inlineCheckbox1"
              onChange={onCheckboxChange}
              checked={company.isFuelActive}
              value={company.isFuelActive}
            />
          </div>
          <p className="text-sm font-medium mt-3 dark:text-white text-labels">
            {t("masters.fields.enabeldAssetTypes")}&nbsp;:
          </p>
          <div className="grid grid-cols-3 gap-5">
            {assetTypes.map((x) => (
              <div
                key={x.id}
                className="flex gap-2 items-center h-full justify-start"
              >
                <input
                  name="isFuelActive"
                  type="checkbox"
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                  id="inlineCheckbox1"
                  onChange={() => onAssetTypeCheckBoxChange(x.id)}
                  checked={company.enabledAssetTypes.includes(x.id)}
                  value={company.enabledAssetTypes.includes(x.id)}
                />
                <p className="text-sm font-medium dark:text-white text-black">
                  {x.name}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="flex relative h-fit left-[100px] top-[50px] bg-transparent justify-center items-center">
          <input
            type="file"
            accept="image/*"
            className="absolute documents cursor-pointer h-10 w-10 border-0 focus:outline-none opacity-0"
            onChange={handleFileChange}
          />
          {imageLoading ? (
            <div>
              <span class="inline-flex items-center gap-px">
                <span class="animate-blink mx-px h-1.5 w-1.5 rounded-full bg-neutral-600"></span>
                <span class="animate-blink animation-delay-200 mx-px h-1.5 w-1.5 rounded-full bg-neutral-600"></span>
                <span class="animate-blink animation-delay-[400ms] mx-px h-1.5 w-1.5 rounded-full bg-neutral-600"></span>
              </span>
            </div>
          ) : company.imageId && !company.imageId.startsWith("0000") ? (
            <img
              src={`${BASE_FILE_URL}/${company.imageId}`}
              alt="companyLogo"
              className="h-full max-h-56 w-auto image-full hover:fill-white"
            />
          ) : (
            <div className="w-12 h-10 dark:bg-neutral-700 bg-gray2 rounded flex justify-center items-center">
              <img
                src="upload_asset_logo.svg"
                alt="companyLogo"
                className=" w-6 hover:fill-white"
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-center items-center pb-8">
        <div className="flex justify-center form-check form-check-inline mr-2">
          <label
            className="form-check-label inline-block placeholder:text-white dark:text-white text-labels mr-3 mt-1"
            forhtml="inlineCheckbox1"
          >
            {t("masters.fields.active")}
          </label>
          <input
            name="active"
            className="focus:ring-blue-500 h-4 lg:h-5 w-4 lg:w-5 text-blue-600 border-gray-300 rounded mt-1"
            type="checkbox"
            id="inlineCheckbox1"
            onChange={onCheckboxChange}
            checked={company.active}
            value={company.active}
          ></input>
        </div>
        <button
          type="button"
          name="Cancel"
          onClick={() => navigate("/companyList")}
          className="dark:bg-white bg-closeBtns dark:text-black text-closeBtnTxts text-sm rounded-4xl w-48 h-9 justify-center mr-3"
        >
          {t("button.cancel")}
        </button>
        <button
          type="button"
          name="create"
          className="dark:bg-indigo-700 bg-submitBtns dark:text-white text-submitBtnTxts text-sm rounded-4xl w-48 h-9 disabled:opacity-40 justify-center"
          onClick={() => onSave()}
          disabled={loading}
        >
          {id === "new" ? t("button.create") : t("button.update")}
        </button>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <ToastContainer />
    </div>
  );
};

export default CompanyForm;
